// #189：登陆超时（token 过期）统一处理。
// 后台各页面 HTTP 调用方式不一（原生 fetch / 内联 authFetch / api client），
// 历史上仅部分页面处理 401，导致 token 失效后页面卡死、报错而不跳登录。
// 这里在应用启动时安装一个全局 fetch 拦截器集中处理。
//
// 后续补充：access token 只有 2 小时，此前一到点就把人踢回登录页，正在填的弹窗
// 内容一并丢掉。登录时其实已经签发了 refreshToken（14 天）却从没人用它——
// C/B 两端一直有续期这条路，后台是唯一缺的。现在 401 先静默续期并重放原请求，
// 续期确实失败（refresh 也过期 / 账号被停用 / 身份被取消）才踢回登录页。

const TOKEN_KEY = 'admin_access_token';
const REFRESH_KEY = 'admin_refresh_token';
const ACCOUNT_KEY = 'admin_account';
const REFRESH_URL = '/api/admin/auth/refresh';

let redirecting = false;
/** 并发 401 只触发一次续期，其余请求等这一个结果 */
let refreshing: Promise<string | null> | null = null;

function urlOf(input: RequestInfo | URL): string {
  return typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
}

function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ACCOUNT_KEY);
}

function bailToLogin(): void {
  if (redirecting || window.location.pathname.startsWith('/login')) return;
  redirecting = true;
  clearAuth();
  window.location.href = '/login';
}

/**
 * 用 refreshToken 换一个新的 access token。成功返回新 token，失败返回 null。
 *
 * 用原生 fetch（而不是被包过的 window.fetch）发这个请求：否则它自己返回 401 时
 * 会再次进入拦截器、再次触发续期，转成死循环。
 */
function doRefresh(originalFetch: typeof window.fetch): Promise<string | null> {
  if (refreshing) return refreshing;
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return Promise.resolve(null);

  refreshing = originalFetch(REFRESH_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: rt }),
  })
    .then(async (r) => {
      if (!r.ok) return null;
      const body = (await r.json()) as {
        accessToken?: string;
        account?: unknown;
      };
      if (!body.accessToken) return null;
      localStorage.setItem(TOKEN_KEY, body.accessToken);
      // 身份可能在这 2 小时里被改过，顺便刷新本地账号信息
      if (body.account) localStorage.setItem(ACCOUNT_KEY, JSON.stringify(body.account));
      return body.accessToken;
    })
    .catch(() => null)
    .finally(() => {
      // 无论成败都释放，下一次 401 才能重新尝试
      refreshing = null;
    });

  return refreshing;
}

export function installAuthInterceptor(): void {
  if (typeof window === 'undefined' || !window.fetch) return;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Request 对象的 body 是一次性流，重放前必须先留一份副本
    const replayable = input instanceof Request ? input.clone() : null;

    const res = await originalFetch(input, init);
    if (res.status !== 401) return res;

    const url = urlOf(input);
    // 续期端点自身 401 说明 refresh 也过期了，不能再套娃
    if (!url.includes('/api/') || url.includes(REFRESH_URL)) {
      if (url.includes(REFRESH_URL)) bailToLogin();
      return res;
    }
    if (window.location.pathname.startsWith('/login')) return res;

    const token = await doRefresh(originalFetch);
    if (!token) {
      bailToLogin();
      return res;
    }

    // 拿到新 token，用它重放一次原请求
    if (replayable) {
      const headers = new Headers(replayable.headers);
      headers.set('authorization', `Bearer ${token}`);
      return originalFetch(new Request(replayable, { headers }));
    }
    const headers = new Headers(init?.headers);
    headers.set('authorization', `Bearer ${token}`);
    return originalFetch(input, { ...init, headers });
  };
}
